/**
 *
 *  TrainerCard
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 30/11/2016.
 *  Copyright (c) 2016-present iWoo Ltd. All rights reserved.
 *
 */

/* eslint no-undef: "error" */

import React, { useCallback, useState } from "react";
import PropTypes from "prop-types"; // use directly

// Components
import Button from "../Button/Button";
import TrainerRating from "../TrainerRating/TrainerRating";
import ProductBadge from "./ProductBadge/ProductBadge";
import ClampLines from "../ClampLines/ClampLines";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import CalendarIcon from "../../icons/CalendarTruBeIcon";

import defaultPartnerPhoto from "../../images/generic-trainerphoto.svg";

// Utils
import { getPartnerPictureUrl } from "../../utils";

export const TrainerCard = React.memo(
  ({
    className,
    timeSlotsActive,
    showCta,
    trainer,
    isOnline,
    onBookWithMe,
    onTimeSlotsClick,
    onCardClick,
    // theme,
  }) => {
    const { theme } = useTheme();

    const [_timeSlotsActive, setTimeSlotsActive] = useState(timeSlotsActive);

    const trainerPictureUrl = trainer && getPartnerPictureUrl(trainer);
    const trainerProducts = trainer && trainer.products;
    const trainerFirstName = trainer && trainer.firstName;
    const trainerFamilyName = trainer && trainer.lastName;
    const trainerHeadline = trainer && trainer.headline;
    const trainerRating = trainer && trainer.rating;
    const trainerNumberOfRatings = (trainer && trainer.reviews) || 0;

    const price = trainer
      ? `${trainer.price.symbol || "£"}${trainer.price.value}`
      : "";

    const handleBookWithMeClick = useCallback(
      (e) => {
        // e.stopPropagation();

        onBookWithMe && onBookWithMe(e, trainer);
      },
      [trainer, onBookWithMe]
    );

    const handleTimeSlotsClick = useCallback(
      (e) => {
        e.stopPropagation();

        onTimeSlotsClick && onTimeSlotsClick(e, trainer);
      },
      [trainer, onTimeSlotsClick]
    );

    const handleCardClick = useCallback((e) => {
      onCardClick && onCardClick(e, trainer);
    });

    const handleMouseEnter = useCallback(() => {
      if (!_timeSlotsActive) {
        setTimeSlotsActive(true);
      }
    }, [_timeSlotsActive]);

    const handleMouseOut = useCallback(() => {
      if (_timeSlotsActive) {
        setTimeSlotsActive(false);
      }
    }, [_timeSlotsActive]);

    const numberOfRatings = 121;

    return (
      <div
        className={`${
          className ? className + " " : ""
        }tb-trainer-card tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-shadow-trube md:tbk-h-52${
          showCta ? " show-cta" : ""
        } tbk-cursor-pointer`}
        onClick={handleCardClick}
      >
        <ul className="tb-disciplines-list tbk-mx-3 tbk-mb-0 tbk-hidden !tbk-hidden tbk-items-center md:tbk-flex">
          {trainerProducts && trainerProducts.length
            ? trainerProducts
                .slice(0, 3)
                .map((prod) => (
                  <ProductBadge
                    key={prod.id}
                    className="tbk-text-small-bold tbk-mr-1 tbk-flex tbk-items-center tbk-whitespace-nowrap tbk-rounded-b-lg tbk-border-l tbk-border-r tbk-border-b tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white tbk-px-[10px] tbk-py-0.5 tbk-uppercase tbk-text-primary"
                    iconClassName="tbk-mr-1"
                    workoutLabel={prod.name}
                  />
                ))
            : null}
        </ul>
        <div className="tb-trainer-card-body tbk-pt-2_ tbk-relative tbk-flex tbk-h-full tbk-flex-col md:tbk-flex-row md:tbk-items-end md:tbk-pt-0">
          <div className="tb-profile-picture tbk--mr-2_ tbk-basis-38_ tbk-overflow-hidden_ tbk-h-85 tbk-shrink-0 tbk-grow-0 md:tbk-m-0 md:tbk-h-52 md:tbk-basis-52">
            <img
              className="tb-profile-picture-img img-responsive tbk-object-top_ tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
              itemProp="image"
              src={trainerPictureUrl || defaultPartnerPhoto}
              alt="Trainer profile"
            />
          </div>
          <div
            className="tbk-w-full tbk-min-w-0 tbk-p-2 tbk-pl-2 tbk-pb-1 md:tbk-p-2 md:tbk-pb-3"
            style={
              {
                // minWidth: 0,
              }
            }
          >
            {
              <ul className="tb-disciplines-list tb-mb-2 hidden-xs tbk-flex tbk-items-center">
                <li className="tb-disciplines-skills-br"></li>
              </ul>
            }
            <h4
              itemProp="name"
              className={
                "tb-trainer-card-title tbk-text-h3-subtitle md:tbk-text-bright md:tbk-mb-2_ tbk-mt-0 tbk-mb-0 tbk-text-primary"
              }
            >
              {trainerFirstName} {trainerFamilyName}
            </h4>
            <div className="tbk-items-end md:tbk-flex">
              <div className="flex-grow-1">
                <div className="tb-trainer-card-headline  tbk-mb-1 tbk-mb-1.5 tbk-min-h-[36px] md:tbk-mr-8 md:tbk-mt-0 md:tbk-mb-1 md:tbk-pr-3">
                  <ClampLines
                    text={trainerHeadline}
                    id={"coach-headline"}
                    className="tbk-text-small tbk-text-primary"
                    lines={
                      // clientWidth() < theme.responsive.breakpoints.lg ? 2 : 2
                      2
                    }
                    ellipsis="..."
                    buttons={false}
                  />
                </div>
                {trainerNumberOfRatings || numberOfRatings ? (
                  <TrainerRating
                    className="tbk-text-small-bold tbk-mb-1 tbk-flex tbk-items-center tbk-justify-start md:tbk-mb-2"
                    averageRatingClassName="tbk-text-primary tbk-text-small-bold"
                    numberOfSessionsClassName="tbk-text-grey tbk-text-small"
                    ratingsText={"ratings"}
                    partner={{
                      partnerRatingRecap: {
                        average: trainerRating,
                        numberOfRatings: trainerNumberOfRatings,
                      },
                    }}
                  />
                ) : (
                  <div style={{ minHeight: 43 }} />
                )}
              </div>
              <div className="md:tbk-left-0_ tb-trainer-card-session-price tbk-left-2_ tbk-mb-1_ tbk-absolute tbk-right-2 tbk-bottom-2 tbk-flex tbk-items-center md:tbk-bottom-8 md:tbk-mb-0.5 md:tbk-block md:tbk-text-right lg:tbk-right-2 lg:tbk-left-0">
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-mr-1 tbk-text-primary md:tbk-mr-0">
                  {price}
                </div>
                <div className="tbk-text-small !tbk-leading-none tbk-text-grey">
                  per session
                </div>
              </div>
            </div>

            <div className="tbk-hidden md:tbk-flex md:tbk-justify-between">
              <button
                className={`tbk-flex tbk-items-center tbk-rounded-lg tbk-px-2 tbk-text-green tbk-shadow-trube hover:tbk-bg-green hover:tbk-text-basic-white`}
                onMouseEnter={handleMouseEnter}
                onMouseOut={handleMouseOut}
                onClick={handleTimeSlotsClick}
              >
                <CalendarIcon className="tbk-pointer-events-none tbk-shrink-0" />
                <span
                  className={`tbk-text-small-bold tbk-ml-1 tbk-uppercase ${
                    _timeSlotsActive
                      ? "tbk-text-basic-white"
                      : "tbk-text-primary"
                  } tbk-pointer-events-none`}
                >
                  Availability
                </span>
              </button>
              {true || showCta ? (
                <Button
                  padding=""
                  variant={"secondary"}
                  size={"md"}
                  color={"coral"}
                  className="tbk-rounded-lg"
                  onClick={handleBookWithMeClick}
                >
                  View profile
                </Button>
              ) : null}
            </div>
          </div>

          <div
            className="hidden"
            itemProp="offers"
            itemScope
            itemType="http://schema.org/Offer"
          >
            <meta itemProp="priceCurrency" content="GBP" />
            <meta itemProp="price" content="40" />
          </div>
          <meta itemProp="brand" content="TruBe" />
        </div>
        <div className="tb-trainer-card-footer tbk-flex tbk-justify-evenly tbk-p-1 tbk-shadow-trube md:tbk-hidden">
          <button
            className={`tbk-text-h3-subtitle tbk-flex tbk-w-full tbk-min-w-0 tbk-items-center tbk-justify-center tbk-rounded-lg tbk-px-2 tbk-shadow-trube ${
              _timeSlotsActive
                ? "tbk-bg-green tbk-text-basic-white"
                : "tbk-text-primary"
            } tbk-z-1 tbk-border tbk-border-solid tbk-border-blue-grey-light`}
            style={{
              paddingTop: "6px",
              paddingBottom: "6px",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseOut={handleMouseOut}
            onClick={handleTimeSlotsClick}
          >
            <span className="tbk-pointer-events-none tbk-whitespace-nowrap">
              Availability
            </span>
            <CalendarIcon
              className={`tbk-shrink-0 ${
                _timeSlotsActive ? "tbk-text-basic-white" : "tbk-text-green"
              } tbk-pointer-events-none`}
            />
          </button>
          <button
            className="tbk-text-h3-subtitle tbk-ml-1 tbk-w-full tbk-min-w-0 tbk-rounded-lg tbk-bg-coral tbk-px-2 tbk-text-basic-white hover:tbk-bg-coral-light"
            style={{
              paddingTop: "6px",
              paddingBottom: "6px",
            }}
            onClick={handleBookWithMeClick}
          >
            <span className="tbk-whitespace-nowrap">View profile</span>
          </button>
        </div>
      </div>
    );
  }
);

TrainerCard.propTypes = {
  // theme: PropTypes.object.isRequired,
  showCta: PropTypes.bool,
  trainer: PropTypes.shape({
    firstName: PropTypes.string,
    id: PropTypes.number,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    region: PropTypes.string,
  }).isRequired,
  locationType: PropTypes.number,
  // onBookWithMe: PropTypes.func.isRequired,
};

export default TrainerCard;
